import React, { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, Mail, MessageSquare, Search, Send, Trash2, Truck } from "lucide-react";

const STATUSES = [
  "created",
  "pending_payment_setup",
  "paid",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
];

const STATUS_COLORS = {
  created: "bg-[#ebe5df] text-[#1a1918]",
  pending_payment_setup: "bg-[#fdfbf7] border border-[#c58b76] text-[#c58b76]",
  paid: "bg-[#365e47] text-white",
  processing: "bg-[#c58b76] text-white",
  shipped: "bg-[#1a1918] text-white",
  delivered: "bg-[#365e47] text-white",
  cancelled: "bg-[#8b3a36] text-white",
};

const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const ymd = (s) => (s ? new Date(s).toLocaleDateString("en-IN") : "—");

export function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [draft, setDraft] = useState({
    status: "",
    tracking_number: "",
    courier: "",
    admin_notes: "",
    notify_customer: true,
  });
  const [saving, setSaving] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [customMsg, setCustomMsg] = useState({ subject: "", message: "", channels: ["email"] });

  const load = () => {
    const params = {};
    if (statusFilter && statusFilter !== "all") params.status_filter = statusFilter;
    if (search.trim()) params.q = search.trim();
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data));
  };
  useEffect(load, [statusFilter]); // eslint-disable-line

  const openOrder = async (id) => {
    setActiveId(id);
    const { data } = await api.get(`/admin/orders/${id}`);
    setDetail(data);
    setDraft({
      status: data.order.status,
      tracking_number: data.order.tracking_number || "",
      courier: data.order.courier || "",
      admin_notes: data.order.admin_notes || "",
      notify_customer: true,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const fresh = await api.patch(`/admin/orders/${activeId}`, draft);
      setDetail((d) => ({ ...d, order: fresh.data }));
      toast.success(
        draft.notify_customer
          ? "Order updated · customer notification sent (skipped if provider keys missing)"
          : "Order updated"
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const sendCustom = async () => {
    if (!customMsg.subject.trim() || !customMsg.message.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    try {
      await api.post(`/admin/orders/${activeId}/notify`, customMsg);
      toast.success("Message dispatched (skipped if provider keys missing)");
      setMsgOpen(false);
      setCustomMsg({ subject: "", message: "", channels: ["email"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 justify-between items-end mb-6">
        <div>
          <h2 className="font-serif text-3xl">Orders</h2>
          <p className="text-sm text-[#5c5a56] mt-1">
            {orders.length} order{orders.length === 1 ? "" : "s"} · manual status & shipping updates
          </p>
        </div>
        <div className="flex gap-3 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5c5a56]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Name, email, phone, ID…"
              className="pl-9 pr-3 py-2 border border-[#ebe5df] bg-white text-sm focus:outline-none focus:border-[#c58b76]"
              data-testid="orders-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-none" data-testid="orders-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Order</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Items</TableHead>
            <TableHead>Total</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Placed</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map((o) => (
            <TableRow
              key={o.id}
              onClick={() => openOrder(o.id)}
              className="cursor-pointer hover:bg-[#fdfbf7]"
              data-testid={`order-row-${o.id}`}
            >
              <TableCell className="font-mono text-xs">#{o.id.slice(0, 8)}</TableCell>
              <TableCell>
                <div className="font-medium">{o.shipping_name}</div>
                <div className="text-xs text-[#5c5a56]">{o.shipping_email}</div>
              </TableCell>
              <TableCell>{o.items?.length || 0}</TableCell>
              <TableCell>{inr(o.total)}</TableCell>
              <TableCell>
                <span
                  className={`text-[10px] tracking-widest uppercase px-2 py-1 ${
                    STATUS_COLORS[o.status] || ""
                  }`}
                >
                  {o.status?.replace(/_/g, " ")}
                </span>
              </TableCell>
              <TableCell className="text-sm">{ymd(o.created_at)}</TableCell>
            </TableRow>
          ))}
          {orders.length === 0 && (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-[#5c5a56] py-12">
                No orders yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={!!activeId} onOpenChange={(o) => { if (!o) { setActiveId(null); setDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Order #{detail?.order?.id?.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="overline mb-2">Customer</h3>
                <div className="text-sm space-y-1">
                  <div className="font-medium text-base">{detail.customer?.name || detail.order.shipping_name}</div>
                  <div className="text-[#5c5a56]">{detail.customer?.email || detail.order.shipping_email}</div>
                  <div className="text-[#5c5a56]">{detail.customer?.phone || detail.order.shipping_phone || "no phone"}</div>
                </div>
                <h3 className="overline mb-2 mt-6">Shipping</h3>
                <div className="text-sm text-[#3d3b38] whitespace-pre-wrap">
                  {detail.order.shipping_name}
                  {"\n"}
                  {detail.order.shipping_address}
                  {"\n"}
                  {detail.order.shipping_phone}
                </div>
                <h3 className="overline mb-2 mt-6">Items</h3>
                <table className="w-full text-sm">
                  <tbody>
                    {detail.order.items.map((i, idx) => (
                      <tr key={idx} className="border-b border-[#ebe5df]">
                        <td className="py-2">
                          {i.name}
                          <span className="text-[#5c5a56]"> × {i.quantity}</span>
                        </td>
                        <td className="py-2 text-right">{inr(i.price * i.quantity)}</td>
                      </tr>
                    ))}
                    {detail.order.coupon && (
                      <tr>
                        <td className="py-2 text-[#c58b76]">Discount ({detail.order.coupon.code})</td>
                        <td className="py-2 text-right text-[#c58b76]">-{inr(detail.order.discount)}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="py-3 font-serif text-lg">Total</td>
                      <td className="py-3 text-right font-serif text-lg">{inr(detail.order.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h3 className="overline mb-2">Manual update</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Status</Label>
                    <Select
                      value={draft.status}
                      onValueChange={(v) => setDraft({ ...draft, status: v })}
                    >
                      <SelectTrigger data-testid="order-detail-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tracking number</Label>
                    <Input
                      value={draft.tracking_number}
                      onChange={(e) => setDraft({ ...draft, tracking_number: e.target.value })}
                      data-testid="order-detail-tracking"
                    />
                  </div>
                  <div>
                    <Label>Courier</Label>
                    <Input
                      value={draft.courier}
                      onChange={(e) => setDraft({ ...draft, courier: e.target.value })}
                      placeholder="BlueDart, Delhivery…"
                      data-testid="order-detail-courier"
                    />
                  </div>
                  <div>
                    <Label>Admin notes (internal)</Label>
                    <Textarea
                      rows={2}
                      value={draft.admin_notes}
                      onChange={(e) => setDraft({ ...draft, admin_notes: e.target.value })}
                      data-testid="order-detail-notes"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={draft.notify_customer}
                      onChange={(e) => setDraft({ ...draft, notify_customer: e.target.checked })}
                      data-testid="order-detail-notify"
                    />
                    Notify customer (email + WhatsApp)
                  </label>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={save} disabled={saving} className="rounded-none flex-1" data-testid="order-detail-save">
                      <Truck size={14} className="mr-1.5" />
                      {saving ? "Saving…" : "Save & notify"}
                    </Button>
                    <Button variant="outline" onClick={() => setMsgOpen(true)} className="rounded-none" data-testid="order-detail-message">
                      <Send size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Custom message modal */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Send message to customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Subject</Label>
              <Input
                value={customMsg.subject}
                onChange={(e) => setCustomMsg({ ...customMsg, subject: e.target.value })}
                data-testid="custom-msg-subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                rows={5}
                value={customMsg.message}
                onChange={(e) => setCustomMsg({ ...customMsg, message: e.target.value })}
                data-testid="custom-msg-body"
              />
            </div>
            <div className="flex gap-4">
              {["email", "whatsapp"].map((ch) => (
                <label key={ch} className="flex items-center gap-2 text-sm capitalize">
                  <input
                    type="checkbox"
                    checked={customMsg.channels.includes(ch)}
                    onChange={(e) => {
                      const next = new Set(customMsg.channels);
                      e.target.checked ? next.add(ch) : next.delete(ch);
                      setCustomMsg({ ...customMsg, channels: Array.from(next) });
                    }}
                    data-testid={`custom-msg-${ch}`}
                  />
                  {ch === "email" ? <Mail size={14} /> : <MessageSquare size={14} />}
                  {ch}
                </label>
              ))}
            </div>
            <Button onClick={sendCustom} className="rounded-none w-full" data-testid="custom-msg-send">
              Send now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [customMsg, setCustomMsg] = useState({ subject: "", message: "", channels: ["email"] });

  const load = () => {
    const params = {};
    if (search.trim()) params.q = search.trim();
    api.get("/admin/customers", { params }).then((r) => setCustomers(r.data));
  };
  useEffect(load, []); // eslint-disable-line

  const openCustomer = async (id) => {
    setActiveId(id);
    const { data } = await api.get(`/admin/customers/${id}`);
    setDetail(data);
  };

  const downloadCSV = () => window.open(`${BACKEND_URL}/api/admin/customers/export.csv?token=${localStorage.getItem("kk_token")}`, "_blank");
  const downloadXLSX = () => window.open(`${BACKEND_URL}/api/admin/customers/export.xlsx?token=${localStorage.getItem("kk_token")}`, "_blank");

  // We rely on the bearer header; the simplest cross-browser approach for an authed download
  // is to fetch the blob and open it. Override the simple window.open above:
  const downloadFile = async (path, filename) => {
    const res = await fetch(`${BACKEND_URL}${path}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("kk_token")}` },
    });
    if (!res.ok) {
      toast.error("Download failed");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const sendCustom = async () => {
    if (!customMsg.subject.trim() || !customMsg.message.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    try {
      await api.post(`/admin/customers/${activeId}/notify`, customMsg);
      toast.success("Message dispatched (skipped if provider keys missing)");
      setMsgOpen(false);
      setCustomMsg({ subject: "", message: "", channels: ["email"] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const deleteCustomer = async (cid, customerName) => {
    const includeOrders = window.confirm(
      `Delete customer "${customerName}"?\n\n` +
      `· OK = also delete their order history (irreversible).\n` +
      `· Cancel here, then click the row again, to keep orders for accounting.`
    );
    // If user cancelled the first dialog, ask a second simpler one to delete account only
    let deleteOrders = false;
    if (includeOrders) {
      deleteOrders = true;
    } else {
      const accountOnly = window.confirm(
        `Delete only the account for "${customerName}" and keep their order history?`
      );
      if (!accountOnly) return;
      deleteOrders = false;
    }
    try {
      const { data } = await api.delete(`/admin/customers/${cid}`, {
        params: { delete_orders: deleteOrders },
      });
      toast.success(
        `Customer removed${data.deleted_orders ? ` · ${data.deleted_orders} orders deleted` : ""}`
      );
      if (activeId === cid) {
        setActiveId(null);
        setDetail(null);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 justify-between items-end mb-6">
        <div>
          <h2 className="font-serif text-3xl">Customer master</h2>
          <p className="text-sm text-[#5c5a56] mt-1">
            {customers.length} customer{customers.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5c5a56]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Name, email, phone"
              className="pl-9 pr-3 py-2 border border-[#ebe5df] bg-white text-sm focus:outline-none focus:border-[#c58b76]"
              data-testid="customers-search"
            />
          </div>
          <Button
            onClick={() => downloadFile("/api/admin/customers/export.csv", "konokangen-customers.csv")}
            variant="outline"
            className="rounded-none"
            data-testid="download-csv"
          >
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button
            onClick={() => downloadFile("/api/admin/customers/export.xlsx", "konokangen-customers.xlsx")}
            className="rounded-none"
            data-testid="download-xlsx"
          >
            <Download size={14} className="mr-1.5" /> Excel
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Orders</TableHead>
            <TableHead>Total spent</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((c) => (
            <TableRow
              key={c.id}
              onClick={() => openCustomer(c.id)}
              className="cursor-pointer hover:bg-[#fdfbf7]"
              data-testid={`customer-row-${c.id}`}
            >
              <TableCell className="font-medium">{c.name}</TableCell>
              <TableCell className="text-sm">{c.email}</TableCell>
              <TableCell className="text-sm">{c.phone || "—"}</TableCell>
              <TableCell>{c.orders_count}</TableCell>
              <TableCell>{inr(c.total_spent)}</TableCell>
              <TableCell className="text-sm">{ymd(c.created_at)}</TableCell>
              <TableCell className="text-right">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteCustomer(c.id, c.name);
                  }}
                  className="text-[#8b3a36] hover:text-[#1a1918]"
                  data-testid={`delete-customer-${c.id}`}
                  title="Delete customer"
                >
                  <Trash2 size={16} />
                </button>
              </TableCell>
            </TableRow>
          ))}
          {customers.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-[#5c5a56] py-12">
                No customers yet.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={!!activeId} onOpenChange={(o) => { if (!o) { setActiveId(null); setDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{detail.customer.name}</DialogTitle>
              </DialogHeader>
              <div className="grid md:grid-cols-3 gap-6 mt-2">
                <div className="md:col-span-1 space-y-3">
                  <div>
                    <div className="overline">Email</div>
                    <div className="text-sm">{detail.customer.email}</div>
                  </div>
                  <div>
                    <div className="overline">Phone</div>
                    <div className="text-sm">{detail.customer.phone || "—"}</div>
                  </div>
                  <div>
                    <div className="overline">Member since</div>
                    <div className="text-sm">{ymd(detail.customer.created_at)}</div>
                  </div>
                  <div className="border-t border-[#ebe5df] pt-3">
                    <div className="overline">Orders</div>
                    <div className="font-serif text-3xl">{detail.orders_count}</div>
                  </div>
                  <div>
                    <div className="overline">Lifetime spend</div>
                    <div className="font-serif text-2xl">{inr(detail.total_spent)}</div>
                  </div>
                  <Button
                    onClick={() => setMsgOpen(true)}
                    className="rounded-none w-full mt-4"
                    data-testid="customer-detail-message"
                  >
                    <Send size={14} className="mr-1.5" /> Send message
                  </Button>
                  <Button
                    onClick={() => deleteCustomer(detail.customer.id, detail.customer.name)}
                    variant="outline"
                    className="rounded-none w-full mt-2 text-[#8b3a36] border-[#8b3a36] hover:bg-[#8b3a36] hover:text-white"
                    data-testid="customer-detail-delete"
                  >
                    <Trash2 size={14} className="mr-1.5" /> Delete customer
                  </Button>
                </div>
                <div className="md:col-span-2">
                  <div className="overline mb-3">Order history</div>
                  {detail.orders.length === 0 ? (
                    <div className="text-sm text-[#5c5a56]">No orders yet.</div>
                  ) : (
                    <div className="space-y-3">
                      {detail.orders.map((o) => (
                        <div key={o.id} className="border border-[#ebe5df] p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-mono text-xs">#{o.id.slice(0, 8)}</div>
                              <div className="text-sm mt-1">
                                {o.items?.map((i) => `${i.name} × ${i.quantity}`).join(" · ")}
                              </div>
                              {o.tracking_number && (
                                <div className="text-xs text-[#5c5a56] mt-1">
                                  Tracking: {o.tracking_number} ({o.courier})
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-serif text-lg">{inr(o.total)}</div>
                              <span className={`text-[10px] tracking-widest uppercase px-1.5 py-0.5 mt-1 inline-block ${STATUS_COLORS[o.status] || ""}`}>
                                {o.status?.replace(/_/g, " ")}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Message to {detail?.customer?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Subject</Label>
              <Input
                value={customMsg.subject}
                onChange={(e) => setCustomMsg({ ...customMsg, subject: e.target.value })}
                data-testid="cust-msg-subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                rows={5}
                value={customMsg.message}
                onChange={(e) => setCustomMsg({ ...customMsg, message: e.target.value })}
                data-testid="cust-msg-body"
              />
            </div>
            <div className="flex gap-4">
              {["email", "whatsapp"].map((ch) => (
                <label key={ch} className="flex items-center gap-2 text-sm capitalize">
                  <input
                    type="checkbox"
                    checked={customMsg.channels.includes(ch)}
                    onChange={(e) => {
                      const next = new Set(customMsg.channels);
                      e.target.checked ? next.add(ch) : next.delete(ch);
                      setCustomMsg({ ...customMsg, channels: Array.from(next) });
                    }}
                    data-testid={`cust-msg-${ch}`}
                  />
                  {ch === "email" ? <Mail size={14} /> : <MessageSquare size={14} />}
                  {ch}
                </label>
              ))}
            </div>
            <Button onClick={sendCustom} className="rounded-none w-full" data-testid="cust-msg-send">
              Send
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function DiscountsTab() {
  const [products, setProducts] = useState([]);
  const [scope, setScope] = useState("all");
  const [category, setCategory] = useState("rings");
  const [mode, setMode] = useState("percent"); // percent | amount
  const [percent, setPercent] = useState(15);
  const [amount, setAmount] = useState(500);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  // Per-row inline edit drafts: { [productId]: { price, compare_at_price } }
  const [edits, setEdits] = useState({});

  const load = () => {
    api
      .get("/admin/discounts/summary")
      .then((r) => {
        setProducts(r.data.products || []);
        setEdits({});
      });
  };
  useEffect(() => {
    load();
  }, []);

  const discountedCount = products.filter(
    (p) => p.compare_at_price && p.compare_at_price > p.price
  ).length;

  const apply = async () => {
    setBusy(true);
    try {
      const body = { scope };
      if (scope === "category") body.category = category;
      if (scope === "products") body.product_ids = Array.from(selected);
      if (mode === "percent") {
        if (percent <= 0 || percent > 95) {
          toast.error("Percent must be between 1 and 95");
          setBusy(false);
          return;
        }
        body.percent_off = percent;
      } else {
        if (amount <= 0) {
          toast.error("Amount must be greater than 0");
          setBusy(false);
          return;
        }
        body.amount_off = amount;
      }
      const { data } = await api.post("/admin/discounts/apply", body);
      toast.success(`${data.updated} product${data.updated === 1 ? "" : "s"} updated`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Restore prices and remove the discount on the selected scope?")) return;
    setBusy(true);
    try {
      const body = { scope };
      if (scope === "category") body.category = category;
      if (scope === "products") body.product_ids = Array.from(selected);
      const { data } = await api.post("/admin/discounts/clear", body);
      toast.success(`${data.restored} product${data.restored === 1 ? "" : "s"} restored`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const setEdit = (id, key, value) => {
    setEdits((e) => ({
      ...e,
      [id]: { ...(e[id] || {}), [key]: value },
    }));
  };

  const saveRow = async (p) => {
    const draft = edits[p.id] || {};
    const newPrice =
      draft.price !== undefined && draft.price !== "" ? parseFloat(draft.price) : p.price;
    let newMrp;
    if (draft.compare_at_price === "" || draft.compare_at_price === null) {
      newMrp = null;
    } else if (draft.compare_at_price !== undefined) {
      newMrp = parseFloat(draft.compare_at_price);
    } else {
      newMrp = p.compare_at_price ?? null;
    }
    if (!(newPrice > 0)) {
      toast.error("Selling price must be greater than 0");
      return;
    }
    if (newMrp != null && newMrp <= newPrice) {
      toast.error("MRP must be higher than selling price (or leave empty)");
      return;
    }
    try {
      await api.patch(`/products/${p.id}`, {
        price: newPrice,
        compare_at_price: newMrp,
      });
      toast.success(`${p.name} updated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const clearRow = async (p) => {
    try {
      await api.patch(`/products/${p.id}`, {
        price: p.compare_at_price && p.compare_at_price > p.price ? p.compare_at_price : p.price,
        compare_at_price: null,
      });
      toast.success(`${p.name} restored`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const CATEGORIES = ["rings", "necklaces", "earrings", "bracelets", "pendants"];

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl">Discounts</h2>
          <p className="text-sm text-[#5c5a56] mt-1">
            {discountedCount} of {products.length} pieces are currently on sale
          </p>
        </div>
      </div>

      {/* Bulk discount control */}
      <div className="border border-[#ebe5df] bg-[#fdfbf7] p-6 mb-8">
        <h3 className="overline mb-4">Apply a bulk discount</h3>
        <div className="grid md:grid-cols-[170px_200px_140px_180px_1fr_auto] gap-4 items-end">
          <div>
            <Label>Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="discount-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All products</SelectItem>
                <SelectItem value="category">A category</SelectItem>
                <SelectItem value="products">Selected items</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {scope === "category" ? (
            <div>
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="discount-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : scope === "products" ? (
            <div className="text-sm text-[#5c5a56] self-center pt-5">
              {selected.size} selected
            </div>
          ) : (
            <div />
          )}
          <div>
            <Label>By</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="discount-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">%  Percent</SelectItem>
                <SelectItem value="amount">₹  Amount</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{mode === "percent" ? "Discount %" : "Off each (₹)"}</Label>
            <Input
              type="number"
              min={1}
              max={mode === "percent" ? 95 : undefined}
              value={mode === "percent" ? percent : amount}
              onChange={(e) =>
                mode === "percent"
                  ? setPercent(parseFloat(e.target.value) || 0)
                  : setAmount(parseFloat(e.target.value) || 0)
              }
              data-testid="discount-value"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={apply} disabled={busy} className="rounded-none" data-testid="discount-apply">
              {busy ? "Working…" : "Apply"}
            </Button>
            <Button
              onClick={clear}
              disabled={busy}
              variant="outline"
              className="rounded-none text-[#8b3a36] border-[#8b3a36] hover:bg-[#8b3a36] hover:text-white"
              data-testid="discount-clear"
            >
              Clear
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-[#5c5a56] mt-3">
          Bulk apply captures each product's current price as the MRP, then drops the selling price.
          For a one-off / non-uniform discount, edit the row directly below.
        </p>
      </div>

      <h3 className="overline mb-3">Per-product pricing</h3>
      <p className="text-xs text-[#5c5a56] mb-4">
        Edit the selling price and MRP directly for any item — perfect for custom amounts that don't fit a single %.
      </p>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              {scope === "products" && <span className="overline">Pick</span>}
            </TableHead>
            <TableHead>Product</TableHead>
            <TableHead>Category</TableHead>
            <TableHead className="w-36">Selling price (₹)</TableHead>
            <TableHead className="w-36">MRP (₹)</TableHead>
            <TableHead>Discount</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {products.map((p) => {
            const onSale = p.compare_at_price && p.compare_at_price > p.price;
            const pct = onSale
              ? Math.round(((p.compare_at_price - p.price) / p.compare_at_price) * 100)
              : 0;
            const draft = edits[p.id] || {};
            const editing = draft.price !== undefined || draft.compare_at_price !== undefined;
            return (
              <TableRow key={p.id} data-testid={`discount-row-${p.id}`}>
                <TableCell>
                  {scope === "products" && (
                    <input
                      type="checkbox"
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      data-testid={`discount-select-${p.id}`}
                    />
                  )}
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="capitalize text-sm">{p.category}</TableCell>
                <TableCell>
                  <Input
                    type="number"
                    value={draft.price !== undefined ? draft.price : p.price}
                    onChange={(e) => setEdit(p.id, "price", e.target.value)}
                    className="h-8 text-sm"
                    data-testid={`row-price-${p.id}`}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    placeholder="—"
                    value={
                      draft.compare_at_price !== undefined
                        ? draft.compare_at_price
                        : p.compare_at_price ?? ""
                    }
                    onChange={(e) => setEdit(p.id, "compare_at_price", e.target.value)}
                    className="h-8 text-sm"
                    data-testid={`row-mrp-${p.id}`}
                  />
                </TableCell>
                <TableCell>
                  {onSale ? (
                    <span className="text-[10px] tracking-widest uppercase bg-[#8b3a36] text-white px-2 py-1">
                      −{pct}% · save ₹{(p.compare_at_price - p.price).toLocaleString("en-IN")}
                    </span>
                  ) : (
                    <span className="text-[#5c5a56] text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button
                    onClick={() => saveRow(p)}
                    disabled={!editing}
                    className={`text-xs uppercase tracking-wider mr-3 ${
                      editing ? "text-[#1a1918] hover:text-[#c58b76]" : "text-[#ebe5df] cursor-not-allowed"
                    }`}
                    data-testid={`row-save-${p.id}`}
                  >
                    Save
                  </button>
                  {onSale && (
                    <button
                      onClick={() => clearRow(p)}
                      className="text-xs uppercase tracking-wider text-[#8b3a36] hover:text-[#1a1918]"
                      data-testid={`row-clear-${p.id}`}
                    >
                      Clear
                    </button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}


export function NotificationsTab() {
  const EVENTS = [
    { key: "welcome", label: "Welcome (on signup)", description: "Sent when a customer creates an account." },
    { key: "order_placed", label: "Order placed", description: "When a new order is created." },
    { key: "payment_received", label: "Payment received", description: "When Razorpay confirms payment." },
    { key: "order_shipped", label: "Order shipped", description: "When admin marks an order as shipped." },
    { key: "order_delivered", label: "Order delivered", description: "When admin marks an order as delivered." },
  ];
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/notifications-config").then((r) => setCfg(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  const set = (event, channel, value) => {
    setCfg((c) => ({ ...c, [event]: { ...(c[event] || {}), [channel]: value } }));
  };

  const save = async () => {
    setSaving(true);
    const { email_provider_configured, whatsapp_provider_configured, id, ...payload } = cfg;
    try {
      await api.put("/admin/notifications-config", payload);
      toast.success("Notification rules saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="text-[#5c5a56]">Loading…</div>;

  return (
    <div className="max-w-3xl">
      <h2 className="font-serif text-3xl mb-2">Notifications</h2>
      <p className="text-sm text-[#5c5a56] mb-8">
        Toggle which events trigger emails and WhatsApp messages to customers.
      </p>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className={`p-4 border ${cfg.email_provider_configured ? "border-[#365e47]" : "border-[#c58b76] bg-[#fdfbf7]"}`}>
          <div className="overline" data-testid="email-status">
            <Mail size={14} className="inline mr-1" />
            Email · {cfg.email_provider_configured ? "Resend connected" : "placeholder key"}
          </div>
          <div className="text-xs text-[#5c5a56] mt-2">
            {cfg.email_provider_configured
              ? "Live — emails will be delivered."
              : "Add RESEND_API_KEY in backend/.env to start sending real emails."}
          </div>
        </div>
        <div className={`p-4 border ${cfg.whatsapp_provider_configured ? "border-[#365e47]" : "border-[#c58b76] bg-[#fdfbf7]"}`}>
          <div className="overline" data-testid="whatsapp-status">
            <MessageSquare size={14} className="inline mr-1" />
            WhatsApp · {cfg.whatsapp_provider_configured ? "Twilio connected" : "placeholder keys"}
          </div>
          <div className="text-xs text-[#5c5a56] mt-2">
            {cfg.whatsapp_provider_configured
              ? "Live — WhatsApp messages will be delivered."
              : "Add Twilio credentials in backend/.env to enable WhatsApp."}
          </div>
        </div>
      </div>

      <div className="border border-[#ebe5df]">
        <div className="grid grid-cols-[1fr_120px_120px] gap-2 px-4 py-2 border-b border-[#ebe5df] bg-[#fdfbf7]">
          <div className="overline">Event</div>
          <div className="overline text-center">Email</div>
          <div className="overline text-center">WhatsApp</div>
        </div>
        {EVENTS.map((e) => (
          <div
            key={e.key}
            className="grid grid-cols-[1fr_120px_120px] gap-2 px-4 py-4 border-b border-[#ebe5df] items-center last:border-0"
          >
            <div>
              <div className="font-medium text-sm">{e.label}</div>
              <div className="text-xs text-[#5c5a56]">{e.description}</div>
            </div>
            <div className="text-center">
              <input
                type="checkbox"
                checked={!!cfg[e.key]?.email}
                onChange={(ev) => set(e.key, "email", ev.target.checked)}
                data-testid={`notif-${e.key}-email`}
              />
            </div>
            <div className="text-center">
              <input
                type="checkbox"
                checked={!!cfg[e.key]?.whatsapp}
                onChange={(ev) => set(e.key, "whatsapp", ev.target.checked)}
                data-testid={`notif-${e.key}-whatsapp`}
              />
            </div>
          </div>
        ))}
      </div>

      <Button onClick={save} disabled={saving} className="rounded-none mt-6" data-testid="notif-save">
        {saving ? "Saving…" : "Save rules"}
      </Button>
    </div>
  );
}
